/**
 * Lightweight structured logger.
 * - Tags every record with level/scope/timestamp.
 * - In dev, prints to the console; in prod, only warn/error are emitted.
 * - `reportError` is the single funnel for runtime errors (boundaries, async
 *   try/catch) so future external sinks (Sentry, Logflare) plug in one place.
 */

type Level = "debug" | "info" | "warn" | "error";

interface LogRecord {
  level: Level;
  scope: string;
  message: string;
  ts: string;
  data?: unknown;
}

const isDev = import.meta.env?.DEV ?? false;

function emit(record: LogRecord) {
  if (!isDev && record.level !== "warn" && record.level !== "error") return;
  const fn =
    record.level === "error"
      ? console.error
      : record.level === "warn"
        ? console.warn
        : record.level === "debug"
          ? console.debug
          : console.info;
  fn(`[${record.scope}] ${record.message}`, record.data ?? "");
}

export const logger = {
  debug: (scope: string, message: string, data?: unknown) =>
    emit({ level: "debug", scope, message, ts: new Date().toISOString(), data }),
  info: (scope: string, message: string, data?: unknown) =>
    emit({ level: "info", scope, message, ts: new Date().toISOString(), data }),
  warn: (scope: string, message: string, data?: unknown) =>
    emit({ level: "warn", scope, message, ts: new Date().toISOString(), data }),
  error: (scope: string, message: string, data?: unknown) =>
    emit({ level: "error", scope, message, ts: new Date().toISOString(), data }),
};

/** Report a runtime error from anywhere (boundary, async handler, etc.). */
export function reportError(scope: string, error: unknown, context?: Record<string, unknown>) {
  const err = error instanceof Error ? error : new Error(String(error));
  logger.error(scope, err.message, { stack: err.stack, ...context });
}

/** Install global handlers for unhandled errors + rejections. */
let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    reportError("window.error", e.error ?? e.message, {
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    reportError("unhandledrejection", e.reason);
  });
}